import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { User, Calendar, MessageSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ApprovalAttribution as ApprovalAttributionType } from '../../types';

interface ApprovalAttributionProps {
  attribution?: ApprovalAttributionType;
  status?: string;
  compact?: boolean;
  className?: string;
}

export const ApprovalAttribution: React.FC<ApprovalAttributionProps> = ({
  attribution,
  status,
  compact = false,
  className = ''
}) => {
  if (!attribution?.approved_by && !attribution?.approved_at) {
    return null;
  }

  const approvedDate = attribution.approved_at ? new Date(attribution.approved_at) : null;
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1 text-xs text-gray-600 ${className}`}>
              <User className="h-3 w-3" />
              <span className="truncate max-w-20">
                {attribution.approver_name || attribution.approver_email || 'Unknown'}
              </span>
              {approvedDate && (
                <span className="text-gray-400">
                  {formatDistanceToNow(approvedDate, { addSuffix: true })}
                </span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64">
            <div className="space-y-1">
              <div className="font-medium">
                {isApproved ? 'Approved by' : isRejected ? 'Rejected by' : 'Modified by'}
              </div>
              <div>{attribution.approver_name || attribution.approver_email || 'Unknown User'}</div>
              {approvedDate && (
                <div className="text-sm text-gray-400">
                  {approvedDate.toLocaleString()}
                </div>
              )}
              {attribution.rejection_reason && (
                <div className="text-sm mt-2 p-2 bg-gray-100 rounded">
                  <strong>Reason:</strong> {attribution.rejection_reason}
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Approver info */}
      <div className="flex items-center gap-2 text-sm">
        <User className="h-4 w-4 text-gray-500" />
        <span className="font-medium">
          {isApproved ? 'Approved by' : isRejected ? 'Rejected by' : 'Modified by'}:
        </span>
        <span className="text-gray-700">
          {attribution.approver_name || attribution.approver_email || 'Unknown User'}
        </span>
      </div>

      {/* Approval date */}
      {approvedDate && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span>
            {formatDistanceToNow(approvedDate, { addSuffix: true })}
          </span>
          <span className="text-gray-400">
            ({approvedDate.toLocaleString()})
          </span>
        </div>
      )}

      {/* Rejection reason */}
      {attribution.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <MessageSquare className="h-4 w-4 text-red-600 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-red-800 mb-1">
                Rejection Reason:
              </div>
              <div className="text-sm text-red-700">
                {attribution.rejection_reason}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ApprovalStatusBadgeProps {
  status: string;
  attribution?: ApprovalAttributionType;
  showApprover?: boolean;
  className?: string;
}

export const ApprovalStatusBadge: React.FC<ApprovalStatusBadgeProps> = ({
  status,
  attribution,
  showApprover = true,
  className = ''
}) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'under_review':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const statusColor = getStatusColor(status);
  const hasApprover = attribution?.approved_by && showApprover;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Badge
        variant="outline"
        className={`border ${statusColor} capitalize`}
      >
        {status.replace('_', ' ')}
      </Badge>

      {hasApprover && (
        <ApprovalAttribution
          attribution={attribution}
          status={status}
          compact={true}
        />
      )}
    </div>
  );
};

export default ApprovalAttribution;
